/**
 * Client-side file-save helpers shared by the PDF and media viewers (and any feature that needs to
 * push bytes/a URL to the user's disk). Framework-free so the anchor/blob/revoke logic lives in one
 * place instead of being copy-pasted per viewer, and stays trivially unit-testable.
 */

/** Trigger a browser download of `href` (an object URL or a same-/cross-origin URL) as `fileName`. */
export function saveUrlAsFile(href: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/** Save a Blob to disk under `fileName`, revoking the temporary object URL once navigation started. */
export function saveBlobAsFile(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  saveUrlAsFile(objectUrl, fileName);
  // Revoke on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** Fetch `url` with credentials and save the response as a file. Throws on a non-OK response. */
export async function fetchAndSaveFile(url: string, fileName: string): Promise<void> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  saveBlobAsFile(await response.blob(), fileName);
}
